/**
 * Super-admin Permissions — read-only RBAC matrix viewer.
 *
 * Surfaces the manually-curated permission matrix from
 * `apps/api/src/lib/permissions-matrix.ts` so super-admins (and society
 * admins reading over their shoulder) can audit which role is allowed to
 * do what across every module. Read-only V1 — editing comes later if
 * customers ask for per-society overrides.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Minus } from 'lucide-react';
import { get } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

type Action = 'list' | 'read' | 'create' | 'update' | 'delete';
type Role =
  | 'super_admin'
  | 'admin'
  | 'building_manager'
  | 'accountant'
  | 'treasurer'
  | 'secretary'
  | 'president'
  | 'resident'
  | 'guard'
  | 'vendor';
type Cell = 'all' | 'own' | 'team' | false;

interface MatrixRow {
  module: string;
  group: string;
  description: string;
  actions: Record<Action, Record<Role, Cell>>;
}

interface MatrixResponse {
  roles: Role[];
  role_labels: Record<Role, string>;
  actions: Action[];
  rows: MatrixRow[];
}

const ACTION_LABEL: Record<Action, string> = {
  list: 'List',
  read: 'Read',
  create: 'Create',
  update: 'Update',
  delete: 'Delete'
};

function CellBadge({ value }: { value: Cell }): JSX.Element {
  if (value === false) {
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-vt-surface-2 text-vt-text-muted">
        <Minus className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (value === 'all') {
    return (
      <span
        title="Allowed across society"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-vt-ok-100 text-vt-ok"
      >
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (value === 'own') {
    return (
      <Badge variant="info" title="Allowed for own rows only">
        own
      </Badge>
    );
  }
  // team
  return (
    <Badge variant="warn" title="Allowed for caller's team/gate scope">
      team
    </Badge>
  );
}

export function SuperAdminPermissionsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<Action>('list');

  const matrixQuery = useQuery<MatrixResponse>({
    queryKey: ['superadmin-permissions-matrix'],
    queryFn: () => get<MatrixResponse>('/superadmin/permissions/matrix'),
    staleTime: 5 * 60 * 1000
  });

  const groups = useMemo(() => {
    if (!matrixQuery.data) return [];
    return Array.from(new Set(matrixQuery.data.rows.map((r) => r.group)));
  }, [matrixQuery.data]);

  const filteredRows = useMemo(() => {
    if (!matrixQuery.data) return [];
    const q = search.trim().toLowerCase();
    return matrixQuery.data.rows.filter((r) => {
      if (groupFilter !== 'all' && r.group !== groupFilter) return false;
      if (!q) return true;
      return (
        r.module.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.group.toLowerCase().includes(q)
      );
    });
  }, [matrixQuery.data, search, groupFilter]);

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Permission matrix"
        description="Read-only RBAC reference. Shows which role can do what across every module. Source of truth is enforced by route preHandlers in the API."
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-vt-3 p-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="perm-search" className="text-vt-12 text-vt-text-muted">
              Search
            </Label>
            <Input
              id="perm-search"
              placeholder="Module / description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="perm-group" className="text-vt-12 text-vt-text-muted">
              Group
            </Label>
            <Select
              id="perm-group"
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
            >
              <option value="all">All groups</option>
              {groups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="perm-action" className="text-vt-12 text-vt-text-muted">
              Action
            </Label>
            <Select
              id="perm-action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value as Action)}
            >
              {(['list', 'read', 'create', 'update', 'delete'] as Action[]).map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABEL[a]}
                </option>
              ))}
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-vt-3 text-vt-12 text-vt-text-muted">
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-vt-ok-100 text-vt-ok">
                <Check className="h-3 w-3" />
              </span>
              all
            </span>
            <span className="inline-flex items-center gap-1">
              <Badge variant="info">own</Badge>
              own rows
            </span>
            <span className="inline-flex items-center gap-1">
              <Badge variant="warn">team</Badge>
              team/gate
            </span>
            <span className="inline-flex items-center gap-1">
              <Minus className="h-3.5 w-3.5" />
              denied
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {matrixQuery.isLoading ? (
            <div className="p-vt-4">
              <Skeleton className="h-6 w-1/3" />
              <div className="mt-vt-3 flex flex-col gap-vt-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            </div>
          ) : !matrixQuery.data ? (
            <div className="p-vt-4 text-vt-13 text-vt-text-muted">No matrix available.</div>
          ) : (
            <table className="w-full border-collapse text-vt-13">
              <thead className="sticky top-0 bg-vt-surface-2">
                <tr>
                  <th className="px-vt-3 py-vt-2 text-left text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                    Module
                  </th>
                  {matrixQuery.data.roles.map((role) => (
                    <th
                      key={role}
                      className="px-vt-2 py-vt-2 text-center text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted"
                    >
                      {matrixQuery.data!.role_labels[role]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.module} className="border-t border-vt-border hover:bg-vt-surface-2">
                    <td className="px-vt-3 py-vt-2 align-top">
                      <div className="font-medium text-vt-text">{r.module}</div>
                      <div className="text-vt-11 text-vt-text-muted">{r.group}</div>
                      <div className="mt-1 max-w-md text-vt-11 text-vt-text-muted">
                        {r.description}
                      </div>
                    </td>
                    {matrixQuery.data!.roles.map((role) => (
                      <td key={role} className="px-vt-2 py-vt-2 text-center align-middle">
                        <CellBadge value={r.actions[actionFilter][role]} />
                      </td>
                    ))}
                  </tr>
                ))}
                {filteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={matrixQuery.data.roles.length + 1}
                      className="px-vt-3 py-vt-6 text-center text-vt-13 text-vt-text-muted"
                    >
                      No modules match the filter.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
